import { type Component, createSignal, Show, For } from 'solid-js'
import { Icon } from '@iconify-icon/solid'
import { useAdminEvents } from '../../../shared/providers/coris/coris-admin-events.provider'
import { useNotification } from '../../../shared/notifications'
import type { BulkValidationResult } from '../data/api/admin-events.api-datasource'

interface BulkCreateEventsDialogProps {
  readonly onClose: () => void
  readonly onCreated?: () => void
}

const BulkCreateEventsDialog: Component<BulkCreateEventsDialogProps> = (props) => {
  const { validateBulkEvents, createBulkEvents, downloadBulkTemplate } = useAdminEvents()
  const notification = useNotification()

  const [file, setFile] = createSignal<File | null>(null)
  const [isDragging, setIsDragging] = createSignal(false)
  const [howItWorksOpen, setHowItWorksOpen] = createSignal(false)
  const [validating, setValidating] = createSignal(false)
  const [validation, setValidation] = createSignal<BulkValidationResult | null>(null)

  let fileInputRef: HTMLInputElement | undefined

  const handleFile = async (f: File): Promise<void> => {
    setFile(f)
    setValidation(null)
    setValidating(true)

    const result = await validateBulkEvents(f)
    setValidation(result)
    setValidating(false)

    if (!result.valid) {
      notification.warn(<span>{result.message ?? `${result.errors.length} validation errors found`}</span>)
    } else {
      notification.success(<span>{result.summary?.total_valid ?? 0} events ready to create</span>)
    }
  }

  const handleFileDrop = (e: DragEvent): void => {
    e.preventDefault()
    setIsDragging(false)
    const dropped = e.dataTransfer?.files?.[0]
    if (dropped) handleFile(dropped)
  }

  const handleFileSelect = (e: Event): void => {
    const input = e.target as HTMLInputElement
    const selected = input.files?.[0]
    if (selected) handleFile(selected)
    input.value = ''
  }

  const handleCreate = (): void => {
    const f = file()
    if (!f) return
    notification.progress(<span>Creating events...</span>, {
      task: async () => {
        const result = await createBulkEvents(f)
        if (!result.valid) {
          // Re-surface backend row errors in the dialog so the user can fix and retry.
          // Guard against the user having swapped/removed the file while the request was in flight.
          if (file() === f) {
            setValidation({ valid: false, preview: [], summary: null, errors: result.errors, message: result.message })
          }
          throw new Error(result.message ?? `${result.errors.length} validation errors found`)
        }
        return result
      },
      onSuccess: (result) => {
        props.onCreated?.()
        props.onClose()
        return <span>{result.summary?.total_created ?? result.created.length} events created successfully</span>
      },
      onError: (err) => <span>Failed to create events: {err instanceof Error ? err.message : 'Unknown error'}</span>
    })
  }

  const canCreate = () => validation()?.valid === true && !validating()

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) props.onClose()
      }}
    >
      <div class="w-[640px] max-h-[80vh] bg-white rounded-xl shadow-xl overflow-hidden animate-menu-enter flex flex-col">
        {/* Header */}
        <div class="px-6 py-5 flex items-center justify-between shrink-0">
          <h2 class="text-base font-semibold text-gray-900">Create events in bulk</h2>
          <button
            type="button"
            class="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-150"
            onClick={props.onClose}
          >
            <Icon icon="heroicons:x-mark" class="text-[1rem]" />
          </button>
        </div>

        {/* Body */}
        <div class="flex-1 overflow-y-auto px-6 pb-5 flex flex-col gap-4">
          {/* File upload zone */}
          <Show
            when={!file()}
            fallback={
              <div class="p-4 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3">
                <div class="w-12 h-12 relative shrink-0 rounded overflow-hidden">
                  <div class="absolute inset-0 bg-green-600 rounded" />
                  <div class="absolute right-0 top-0 w-4 h-4 bg-green-200 rounded-bl" />
                  <span class="absolute bottom-1.5 left-1.5 text-white/90 text-xs font-semibold">CSV</span>
                </div>
                <div class="flex-1 flex flex-col gap-0.5 min-w-0">
                  <span class="text-sm font-semibold text-gray-900 truncate">{file()!.name}</span>
                  <Show when={validating()}>
                    <span class="text-xs text-gray-500 flex items-center gap-1">
                      <div class="w-3 h-3 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
                      Validating...
                    </span>
                  </Show>
                  <Show when={validation()?.valid}>
                    <span class="text-xs text-green-600 flex items-center gap-1">
                      <Icon icon="heroicons:check-circle" class="text-[0.875rem]" />
                      {validation()!.summary?.total_valid} events valid
                    </span>
                  </Show>
                  <Show when={validation() && !validation()!.valid}>
                    <span class="text-xs text-red-500 flex items-center gap-1">
                      <Icon icon="heroicons:exclamation-circle" class="text-[0.875rem]" />
                      {validation()!.errors.length} errors
                    </span>
                  </Show>
                </div>
                <button
                  type="button"
                  class="w-8 h-8 rounded-md flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-150"
                  onClick={() => {
                    setFile(null)
                    setValidation(null)
                  }}
                >
                  <Icon icon="heroicons:x-mark" class="text-[1rem]" />
                </button>
              </div>
            }
          >
            <div
              class="px-4 py-8 bg-gray-50 rounded-lg border border-dashed flex flex-col items-center gap-4 transition-colors duration-200 cursor-pointer"
              classList={{
                'border-primary-400 bg-primary-50': isDragging(),
                'border-gray-300': !isDragging()
              }}
              onDragOver={(e) => {
                e.preventDefault()
                setIsDragging(true)
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                class="hidden"
                onChange={handleFileSelect}
              />
              <div class="text-gray-400">
                <Icon icon="heroicons:cloud-arrow-up" class="text-[2.5rem]" />
              </div>
              <div class="flex flex-col items-center gap-1">
                <p class="text-xl font-bold text-gray-900">Drop your file here or click to browse</p>
                <p class="text-xs font-semibold text-gray-500">Supports .xlsx, .xls, .csv</p>
              </div>
              <button
                type="button"
                class="min-w-24 px-3.5 py-2.5 bg-primary-600 text-white rounded-md shadow-sm text-sm font-semibold hover:bg-primary-700 transition-colors duration-150"
                onClick={(e) => {
                  e.stopPropagation()
                  fileInputRef?.click()
                }}
              >
                Choose file
              </button>
            </div>
          </Show>

          {/* Validation errors */}
          <Show when={validation() && !validation()!.valid && validation()!.errors.length > 0}>
            <div class="bg-red-50 border border-red-200 rounded-lg p-4 flex flex-col gap-2 max-h-48 overflow-y-auto">
              <div class="flex items-center gap-2 text-sm font-semibold text-red-700">
                <Icon icon="heroicons:exclamation-circle" class="text-[1rem]" />
                Validation errors
              </div>
              <For each={validation()!.errors}>
                {(err) => (
                  <div class="text-xs text-red-600 pl-6">
                    <span class="font-medium">Row {err.row}</span>
                    <Show when={err.title}>
                      <span class="text-red-400"> ({err.title})</span>
                    </Show>
                    : {err.field} — {err.message}
                  </div>
                )}
              </For>
            </div>
          </Show>

          {/* How It Works accordion */}
          <div class="bg-gray-50 rounded-lg">
            <button
              type="button"
              class="w-full p-4 flex items-center justify-between"
              onClick={() => setHowItWorksOpen(!howItWorksOpen())}
            >
              <div class="flex items-center gap-2">
                <Icon icon="heroicons:document-text" class="text-[1.25rem] text-gray-500" />
                <span class="text-sm font-semibold text-gray-900">How It Works</span>
              </div>
              <span
                class="text-gray-500 transition-transform duration-200"
                classList={{ 'rotate-180': howItWorksOpen() }}
              >
                <Icon icon="heroicons:chevron-down" class="text-[1rem]" />
              </span>
            </button>
            <div
              class="grid transition-[grid-template-rows] duration-200 ease-in-out"
              classList={{ 'grid-rows-[1fr]': howItWorksOpen(), 'grid-rows-[0fr]': !howItWorksOpen() }}
            >
              <div class="overflow-hidden">
                <div class="px-4 pb-4 text-sm text-gray-600">
                  <ol class="list-decimal list-inside flex flex-col gap-2">
                    <li>Download the template CSV file using the button below.</li>
                    <li>Fill in your event data following the column format.</li>
                    <li>Upload the completed file — it will be validated automatically.</li>
                    <li>Fix any errors shown, then click Create.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div class="px-6 py-5 bg-white border-t border-gray-100 flex items-center justify-end gap-3 shrink-0">
          <button
            type="button"
            class="min-w-20 px-3 py-2 bg-white rounded-md shadow-sm border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
            onClick={props.onClose}
          >
            Cancel
          </button>
          <div class="flex-1 flex items-center justify-end gap-2.5">
            <button
              type="button"
              class="min-w-20 px-3 py-2 bg-white rounded-md shadow-sm border border-gray-200 flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors duration-150"
              onClick={() => downloadBulkTemplate('csv')}
            >
              <Icon icon="heroicons:arrow-down-tray" class="text-[1rem]" />
              Download template
            </button>
            <button
              type="button"
              class="min-w-20 px-3 py-2 rounded-md text-sm font-semibold transition-colors duration-150"
              classList={{
                'bg-primary-600 text-white hover:bg-primary-700 shadow-sm': canCreate(),
                'bg-primary-600 text-white opacity-40 cursor-not-allowed': !canCreate()
              }}
              disabled={!canCreate()}
              onClick={handleCreate}
            >
              Create
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BulkCreateEventsDialog
